import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import { Box, Grid, Modal } from '@mui/material';
import { useDispatch } from 'react-redux';
import { showProgress } from '../../redux/actions/DealershipActions';
import { ErrorToast, notifySuccess } from '../utils/Utils';
import CustomPopupFooter from '../shared/CustomPopupFooter';
import CustomPopupTopBar from '../shared/CustomPopupTopBar';
import CustomPopupText from '../shared/CustomPopupText';
import CustomTextField from '../shared/CustomTextField';
import CustomRadioGroup from '../shared/CustomRadioGroup';
import CustomUploadField from '../shared/CustomUploadField';
import ShowProgress from '../ui/Backdrop';
import service from '../../apis/service';
import { apiEndpoints } from '../../constants/apiEndpoints';
import { AGENDA_TYPE_OPTIONS, INITIAL_DATA } from './constants';

const modalStyle = {
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '527px',
  height: '90vh',
  backgroundColor: '#F2F4F7',
  boxShadow: 24,
  outline: 'none',
};

const useStyles = makeStyles({
  contentContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    backgroundColor: '#F2F4F7',
  },
  buttonContainer: {
    background: '#FFFFFF',
    height: '64px',
    borderTop: '1px solid #E0E0E0',
    padding: '0 16px',
  },
  radioContainer: {
    marginBottom: '16px',
  },
  fieldBlock: {
    marginBottom: '16px',
  },
});

const AddAgendaPopup = ({ isOpen, close, collectionId, agendaId, fetchCollection, onSave }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [data, setData] = useState(INITIAL_DATA);

  const resetForm = () => setData(INITIAL_DATA);

  const fetchAgendaDetails = useCallback(async () => {
    dispatch(showProgress(true));
    try {
      const response = await service.get(
        apiEndpoints.COLLECTION_RESOURCE_DETAIL.replace('COLLECTION_ID', collectionId).replace('RESOURCE_ID', agendaId),
      );

      const source = response.url || response.source || '';
      const fileName = response.type === 'file' && source ? source.split('/').pop() : '';

      setData({
        name: response.name || '',
        type: response.type || 'file',
        url: response.type === 'link' ? source : '',
        file: null,
        filePreview: '',
        fileName: response.type === 'file' ? fileName : '',
      });
    } catch (error) {
      ErrorToast(error);
      resetForm();
    } finally {
      dispatch(showProgress(false));
    }
  }, [agendaId, collectionId, dispatch]);

  useEffect(() => {
    if (!isOpen) return;

    if (collectionId && agendaId) {
      fetchAgendaDetails();
    } else {
      resetForm();
    }
  }, [isOpen, collectionId, agendaId, fetchAgendaDetails]);

  const handleClose = () => {
    resetForm();
    close();
  };

  const handleData = ({ target: { name, value } }) => {
    setData((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === 'type') {
        return value === 'file' ? { ...updated, url: '' } : { ...updated, file: null, filePreview: '', fileName: '' };
      }

      return updated;
    });
  };

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      setData((prev) => ({
        ...prev,
        file: uploadedFile,
        fileName: uploadedFile.name,
        filePreview: ev.target.result,
      }));
    };

    reader.readAsDataURL(uploadedFile);
  };

  const handleDeleteFile = () => {
    setData((prev) => ({
      ...prev,
      file: null,
      filePreview: '',
      fileName: '',
    }));
  };

  const validateForm = () => {
    const errors = [
      !data.name.trim() && 'Please enter Agenda Name',
      data.type === 'link' && !data.url.trim() && 'Please enter Link',
      data.type === 'file' && !data.file && !data.fileName && 'Please upload a file',
    ].filter(Boolean);

    if (errors.length) {
      ErrorToast(errors[0]);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    dispatch(showProgress(true));
    try {
      const formData = new FormData();
      formData.append('name', data.name.trim());

      const skipType = agendaId && data.type === 'file' && !data.file;
      if (!skipType) {
        formData.append('type', data.type);
      }

      const source = data.type === 'link' ? data.url.trim() : data.file;
      if (source) {
        formData.append('source', source);
      }

      if (!agendaId) {
        formData.append('isAgenda', true);
        await service.post(apiEndpoints.COLLECTION_RESOURCE_BASE_PATH.replace('COLLECTION_ID', collectionId), formData);
      } else {
        await service.put(
          apiEndpoints.COLLECTION_RESOURCE_DETAIL.replace('COLLECTION_ID', collectionId).replace(
            'RESOURCE_ID',
            agendaId,
          ),
          formData,
        );
      }

      notifySuccess('Agenda added successfully');
      onSave?.(data);
      fetchCollection?.();
      handleClose();
    } catch (error) {
      ErrorToast(error);
    } finally {
      dispatch(showProgress(false));
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box sx={modalStyle}>
        <CustomPopupTopBar add={!agendaId} title="Agenda" close={handleClose} />

        <Grid container direction="column" className={classes.contentContainer}>
          {/* TYPE */}
          <Grid item className={classes.radioContainer}>
            <Grid container alignItems="center">
              <Grid item mr={3}>
                <CustomPopupText text="Type" required />
              </Grid>
              <Grid item>
                <CustomRadioGroup
                  name="type"
                  value={data.type}
                  options={AGENDA_TYPE_OPTIONS}
                  handleChange={handleData}
                  row={false}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* AGENDA NAME */}
          <Grid item className={classes.fieldBlock}>
            <CustomPopupText text="Agenda Name" required />
            <CustomTextField name="name" value={data.name} handleChange={handleData} width="100%" />
          </Grid>

          {/* LINK / FILE */}
          {data.type === 'link' ? (
            <Grid item className={classes.fieldBlock}>
              <CustomPopupText text="Link" required />
              <CustomTextField name="url" value={data.url} handleChange={handleData} width="100%" />
            </Grid>
          ) : (
            <Grid item className={classes.fieldBlock}>
              <CustomPopupText text="File" required />
              <CustomUploadField
                name="file"
                value={data.filePreview}
                handleChange={handleFileUpload}
                handleDelete={handleDeleteFile}
                fileName={data.fileName}
                customStyles={{ height: '44px', width: '100%' }}
                wordWrapWidth="180px"
                accept="*/*"
              />
            </Grid>
          )}
        </Grid>

        <div className={classes.buttonContainer}>
          <CustomPopupFooter add onClose={handleClose} saveContest={handleSave} step={1} totalSteps={1} />
        </div>

        <ShowProgress />
      </Box>
    </Modal>
  );
};

export default AddAgendaPopup;
